import React, { useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X, Wrench } from "lucide-react";

export default function AdminShopTime() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");

  const load = async () => {
    const params = status === "all" ? {} : { status };
    const { data } = await api.get("/shop-time", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const approve = async (id) => {
    try { await api.post(`/shop-time/${id}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reject = async () => {
    if (!rejectNotes.trim()) { toast.error("Provide a reason"); return; }
    try {
      await api.post(`/shop-time/${rejectFor.id}/reject`, { admin_notes: rejectNotes });
      toast.success("Rejected");
      setRejectFor(null); setRejectNotes(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-shop-time-page">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Admin</p>
          <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Shop / Other Time</h1>
          <p className="text-sm text-slate-500 mt-1">Approve or reject non-job hours (shop, training, etc.). Employees can't finalize a day until their pending entries are resolved.</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-48" data-testid="shop-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </header>

      <Card className="rounded-2xl border-slate-200">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Employee</th>
                <th className="text-left px-4 py-3 font-semibold">Category</th>
                <th className="text-right px-4 py-3 font-semibold">Hours</th>
                <th className="text-left px-4 py-3 font-semibold">Notes</th>
                <th className="text-center px-4 py-3 font-semibold">Status</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan="7" className="px-4 py-10 text-center text-slate-500"><Wrench className="h-8 w-8 mx-auto mb-2 text-slate-300" />No entries.</td></tr>
              )}
              {items.map((s) => (
                <tr key={s.id} className="border-b border-slate-100" data-testid={`shop-row-${s.id}`}>
                  <td className="px-4 py-3 text-slate-700">{fmtDate(s.date)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{s.employee_name || "—"}</td>
                  <td className="px-4 py-3">{s.category}</td>
                  <td className="px-4 py-3 text-right font-bold">{Number(s.hours).toFixed(2)}h</td>
                  <td className="px-4 py-3 text-slate-600 max-w-xs truncate" title={s.notes}>{s.notes || "—"}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      s.status === "approved" ? "bg-green-100 text-green-700" :
                      s.status === "rejected" ? "bg-red-100 text-red-700" :
                      "bg-amber-100 text-amber-800"
                    }`}>{s.status}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {s.status === "pending" && (
                      <div className="flex justify-end gap-1">
                        <Button size="sm" onClick={() => approve(s.id)} className="bg-green-600 hover:bg-green-700" data-testid={`approve-shop-${s.id}-btn`}>
                          <Check className="h-4 w-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => { setRejectFor(s); setRejectNotes(""); }} data-testid={`reject-shop-${s.id}-btn`}>
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={!!rejectFor} onOpenChange={(o) => !o && setRejectFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject Shop Time</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">{rejectFor?.employee_name} · {rejectFor?.category} · {Number(rejectFor?.hours || 0).toFixed(2)}h</p>
            <Textarea value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} placeholder="Reason for rejection (required)" rows={3} data-testid="shop-reject-notes" />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRejectFor(null)} className="flex-1">Cancel</Button>
              <Button onClick={reject} variant="destructive" className="flex-1" data-testid="shop-reject-confirm">Reject</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
